import React, { useState, useEffect } from "react";
import axios from "axios";
import { X, BedDouble, Heart, Brain, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { EnquiryModal } from "./EnquiryModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SHOW_DELAY_MS = 2000;

export const AvailabilityWidget = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [enquiryOpen, setEnquiryOpen] = useState(false);

  useEffect(() => {
    // Fetch availability then show widget after delay (no persistent dismissal)
    const fetchAvailability = async () => {
      try {
        const response = await axios.get(`${API}/bed-availability`);
        setAvailability(response.data);

        // Show after delay
        setTimeout(() => setIsVisible(true), SHOW_DELAY_MS);
      } catch (error) {
        console.error("Error fetching availability:", error);
      }
    };

    fetchAvailability();
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
  };

  const handleBookVisit = () => {
    setEnquiryOpen(true);
  };

  if (!availability || !isVisible) return null;

  const totalAvailable =
    availability.residential_available +
    availability.nursing_available +
    availability.dementia_available;

  const bedTypes = [
    {
      icon: Heart,
      label: "Residential",
      available: availability.residential_available,
      total: availability.residential_total,
      colour: "text-teal-600",
      bg: "bg-teal-50"
    },
    {
      icon: BedDouble,
      label: "Nursing",
      available: availability.nursing_available,
      total: availability.nursing_total,
      colour: "text-orange-600",
      bg: "bg-orange-50"
    },
    {
      icon: Brain,
      label: "Dementia",
      available: availability.dementia_available,
      total: availability.dementia_total,
      colour: "text-rose-600",
      bg: "bg-rose-50"
    }
  ];

  return (
    <>
      <div
        className="fixed bottom-4 right-4 z-[60] w-[calc(100%-2rem)] sm:w-80 animate-slide-in-right"
        data-testid="availability-widget"
      >
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-4 relative">
            <button
              onClick={handleDismiss}
              aria-label="Close availability widget"
              className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-white/20 transition-colors"
              data-testid="availability-widget-close"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-2 mb-1">
              <div className="bg-white/20 backdrop-blur rounded-full p-1.5">
                <Sparkles className="h-4 w-4" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-wider text-teal-50">
                Live Availability
              </span>
            </div>
            <h3 className="text-lg font-bold leading-tight">
              {totalAvailable > 0
                ? `${totalAvailable} ${totalAvailable === 1 ? "Bed" : "Beds"} Available Now`
                : "Check Bed Availability"}
            </h3>
          </div>

          {/* Body */}
          <div className="p-4">
            <div className="space-y-2 mb-4">
              {bedTypes.map((bed, i) => {
                const Icon = bed.icon;
                return (
                  <div
                    key={i}
                    className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`${bed.bg} ${bed.colour} p-2 rounded-lg`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{bed.label}</span>
                    </div>
                    <div className="text-right">
                      {bed.available > 0 ? (
                        <div>
                          <span className="text-base font-bold text-green-600">
                            {bed.available}
                          </span>
                          <span className="text-xs text-gray-500"> of {bed.total}</span>
                        </div>
                      ) : (
                        <span className="text-xs font-semibold text-gray-500 uppercase bg-gray-200 px-2 py-0.5 rounded">
                          Currently Full
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <Button
              onClick={handleBookVisit}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold shadow-md hover:shadow-lg transition-all"
              data-testid="availability-widget-book-btn"
            >
              {totalAvailable > 0 ? "Reserve Your Place" : "Enquire Now"}
            </Button>

            <p className="text-center text-xs text-gray-500 mt-2">
              Free, no-obligation visit &middot; Call{" "}
              <a
                href="tel:01516775573"
                className="font-semibold text-teal-600 hover:underline"
              >
                0151 677 5573
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Local CSS for slide-in animation */}
      <style>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(120%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>

      <EnquiryModal
        isOpen={enquiryOpen}
        onClose={() => {
          setEnquiryOpen(false);
          handleDismiss();
        }}
        enquiryType="visit"
      />
    </>
  );
};
